import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDownIcon, PhoneIcon, XIcon, ArrowRightIcon } from "lucide-react";
import { Logo } from "../ui/Logo";
import { PRIMARY_NAV, RESOURCE_LINKS, SERVICE_GROUPS } from "../../data/navigation";
import { CONTACT } from "../../data/site";
import { trackCta } from "../../utils/analytics";
type Props = {
  open: boolean;
  onClose: () => void;
};
export function MobileMenu({
  open,
  onClose
}: Props) {
  const [section, setSection] = useState<string | null>(null);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    if (open) document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);
  const toggle = (key: string) => setSection(section === key ? null : key);
  const rowCls = "flex min-h-[56px] w-full items-center justify-between border-b border-line font-display text-2xl font-medium tracking-tight";
  return <AnimatePresence data-id="element-338" data-mp="9wevh9098w3f">
      {open && <motion.div role="dialog" aria-modal="true" aria-label="Site menu" initial={{
      x: "100%"
    }} animate={{
      x: 0
    }} exit={{
      x: "100%"
    }} transition={{
      duration: 0.28,
      ease: [0.23, 1, 0.32, 1]
    }} className="fixed inset-0 z-[70] flex flex-col bg-ink xl:hidden" data-id="element-339" data-mp="9xg7pgt0bomq">
          <div className="shell flex h-[68px] shrink-0 items-center justify-between border-b border-line" data-id="element-340" data-mp="3mdjtt777xoi4">
            <Logo onClick={onClose} data-id="element-341" data-mp="3rcenx5amyzhj" />
            <button type="button" onClick={onClose} aria-label="Close menu" className="grid h-11 w-11 place-items-center rounded-full border border-line-strong" data-id="element-342" data-mp="3rdfw9209p7vk">
              <XIcon className="h-5 w-5" data-id="element-343" data-mp="16m5sbew0qvce" />
            </button>
          </div>

          <nav aria-label="Mobile" className="shell flex-1 overflow-y-auto py-4" data-id="element-344" data-mp="16o8m8a0al8me">
            <button type="button" className={rowCls} aria-expanded={section === "services"} onClick={() => toggle("services")} data-id="element-345" data-mp="32rn7xpfhci7x">
              Services <ChevronDownIcon className={`h-5 w-5 transition-transform duration-200 ease-out ${section === "services" ? "rotate-180" : ""}`} data-id="element-346" data-mp="3co9qg7kc2x6s" />
            </button>
            <AnimatePresence initial={false} data-id="element-347" data-mp="37mkb4fq2vxcw">
              {section === "services" && <motion.div initial={{
            height: 0
          }} animate={{
            height: "auto"
          }} exit={{
            height: 0
          }} transition={{
            duration: 0.22,
            ease: [0.23, 1, 0.32, 1]
          }} className="overflow-hidden" data-id="element-348" data-mp="2jzcc2fj8tkjb">
                  <div className="grid gap-6 py-5 sm:grid-cols-3" data-id="element-349" data-mp="2tq6vlc53u612">
                    {SERVICE_GROUPS.map(g => <div key={g.title} data-id="element-350" data-mp="2owcf0hksq767">
                        <p className="t-eyebrow mb-2 text-muted" data-id="element-351" data-mp="217t55xm3lmc0">{g.title}</p>
                        <ul data-id="element-352" data-mp="1w9rn9g0ert24">
                          {g.items.map(i => <li key={i.href} data-id="element-353" data-mp="1w7nlb3m2l44h">
                              <Link to={i.href} onClick={onClose} className="flex min-h-[44px] items-center text-[16px] text-fg-2 hover:text-fg" data-id="element-354" data-mp="bqefjm52ccno">{i.label}</Link>
                            </li>)}
                        </ul>
                      </div>)}
                  </div>
                  <Link to="/services" onClick={onClose} className="mb-4 inline-flex min-h-[44px] items-center gap-2 text-accent-soft" data-id="element-355" data-mp="6sw33lru7fax">All services <ArrowRightIcon className="h-4 w-4" data-id="element-356" data-mp="ug4b4bgnh2gn" /></Link>
                </motion.div>}
            </AnimatePresence>

            {PRIMARY_NAV.map(n => <Link key={n.href} to={n.href} onClick={onClose} className={rowCls} data-id="element-357" data-mp="lgzbf1tcr32w">{n.label}</Link>)}

            <button type="button" className={rowCls} aria-expanded={section === "resources"} onClick={() => toggle("resources")} data-id="element-358" data-mp="qig9kpkgcbfs">
              Resources <ChevronDownIcon className={`h-5 w-5 transition-transform duration-200 ease-out ${section === "resources" ? "rotate-180" : ""}`} data-id="element-359" data-mp="3b742u46ijsci" />
            </button>
            <AnimatePresence initial={false} data-id="element-360" data-mp="3qi2e6mvft54x">
              {section === "resources" && <motion.ul initial={{
            height: 0
          }} animate={{
            height: "auto"
          }} exit={{
            height: 0
          }} transition={{
            duration: 0.22,
            ease: [0.23, 1, 0.32, 1]
          }} className="overflow-hidden py-2" data-id="element-361" data-mp="3lnemyg91twmn">
                  {RESOURCE_LINKS.map(r => <li key={r.href} data-id="element-362" data-mp="d9xwz2yjk3u9">
                      <Link to={r.href} onClick={onClose} className="flex min-h-[44px] items-center text-[16px] text-fg-2 hover:text-fg" data-id="element-363" data-mp="d8wl4w9pbdbh">{r.label}</Link>
                    </li>)}
                </motion.ul>}
            </AnimatePresence>

            <Link to="/contact" onClick={onClose} className={rowCls} data-id="element-364" data-mp="3ve997kmaow83">Contact</Link>
            <Link to="/client-login" onClick={onClose} className="mt-6 inline-flex min-h-[44px] items-center text-fg-2" data-id="element-365" data-mp="2ozrusuj0d0f0">Client Login</Link>
          </nav>

          <div className="grid shrink-0 grid-cols-2 gap-2 border-t border-line bg-ink-2 p-3" data-id="element-366" data-mp="2k28gxvq82pnl">
            <a href={CONTACT.phones[0].href} onClick={() => trackCta("mobile_menu_call", "tel")} className="flex min-h-[52px] items-center justify-center gap-2 rounded-full border border-line-strong font-medium" data-id="element-367" data-mp="zjxx00wwkvku">
              <PhoneIcon className="h-4 w-4" data-id="element-368" data-mp="18gyinzsl8fkl" /> Call
            </a>
            <Link to="/contact" onClick={() => {
          trackCta("mobile_menu_request", "/contact");
          onClose();
        }} className="flex min-h-[52px] items-center justify-center rounded-full bg-accent font-medium text-white" data-id="element-369" data-mp="1db2q0k5rmpm7">
              Request Website
            </Link>
          </div>
        </motion.div>}
    </AnimatePresence>;
}